<script setup lang="ts">
import {
  Archive,
  Undo2,
  Redo2,
  Repeat,
  Split,
  Workflow,
  PawPrint,
  Coffee,
  Volleyball,
  Car,
  Hash,
  IdCard,
  Tag,
  Zap,
  Flag,
  ArrowLeft,
  Ban,
  Bell,
  BellOff,
  Bold,
  BookOpen,
  CalendarDays,
  Camera,
  ChartNoAxesColumnIncreasing,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleCheck,
  CircleDot,
  CircleHelp,
  CircleX,
  Clapperboard,
  Clock3,
  Code2,
  Copy,
  Crop,
  Download,
  EllipsisVertical,
  ExternalLink,
  FileText,
  Forward,
  Globe,
  GripVertical,
  Headphones,
  Heart,
  Image,
  Images,
  Info,
  Italic,
  KeyRound,
  Keyboard,
  LayoutGrid,
  Lightbulb,
  Link,
  List,
  ListFilter,
  LockKeyhole,
  LogOut,
  Mail,
  MapPin,
  Maximize2,
  MessageSquareText,
  MessagesSquare,
  Mic,
  Monitor,
  Moon,
  Palette,
  Pause,
  PenLine,
  Megaphone,
  Wallet,
  TrendingUp,
  Filter,
  Upload,
  WifiOff,
  Pencil,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Pin,
  PinOff,
  Eye,
  EyeOff,
  Play,
  Plus,
  Receipt,
  Reply,
  RotateCcw,
  RotateCw,
  Rocket,
  Search,
  Send,
  SendHorizontal,
  Settings,
  ShieldCheck,
  Smile,
  Sparkles,
  Square,
  Star,
  Sticker,
  Strikethrough,
  Sun,
  Trash2,
  Underline,
  UserRound,
  UserRoundPlus,
  UsersRound,
  Video,
  Volume2,
  X,
} from "@lucide/vue";
const icons = {
  Archive,
  Undo2,
  Redo2,
  Repeat,
  Split,
  Workflow,
  PawPrint,
  Coffee,
  Volleyball,
  Car,
  Hash,
  IdCard,
  Tag,
  Zap,
  Flag,
  ArrowLeft,
  Ban,
  Bell,
  BellOff,
  Bold,
  BookOpen,
  CalendarDays,
  Camera,
  ChartNoAxesColumnIncreasing,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleCheck,
  CircleDot,
  CircleHelp,
  CircleX,
  Clapperboard,
  Clock3,
  Code2,
  Copy,
  Crop,
  Download,
  ExternalLink,
  EllipsisVertical,
  FileText,
  Forward,
  Globe,
  GripVertical,
  Headphones,
  Heart,
  Image,
  Images,
  Info,
  Italic,
  KeyRound,
  Keyboard,
  LayoutGrid,
  Lightbulb,
  Link,
  List,
  ListFilter,
  LockKeyhole,
  LogOut,
  Mail,
  MapPin,
  Maximize2,
  MessageSquareText,
  MessagesSquare,
  Mic,
  Monitor,
  Moon,
  Palette,
  Pause,
  PenLine,
  Megaphone,
  Wallet,
  TrendingUp,
  Filter,
  Upload,
  WifiOff,
  Pencil,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Pin,
  PinOff,
  Eye,
  EyeOff,
  Play,
  Plus,
  Receipt,
  Reply,
  RotateCcw,
  RotateCw,
  Rocket,
  Search,
  Send,
  SendHorizontal,
  Settings,
  ShieldCheck,
  Smile,
  Sparkles,
  Square,
  Star,
  Sticker,
  Strikethrough,
  Sun,
  Trash2,
  Underline,
  UserRound,
  UserRoundPlus,
  UsersRound,
  Video,
  Volume2,
  X,
};
import { computed } from "vue";
const props = withDefaults(
  defineProps<{ name: string; size?: number; stroke?: number }>(),
  { size: 22, stroke: 1.8 },
);
const aliases: Record<string, string> = {
  chat: "MessageSquareText",
  calls: "Phone",
  status: "CircleDot",
  channels: "MessagesSquare",
  communities: "UsersRound",
  media: "Images",
  user: "UserRound",
  search: "Search",
  more: "EllipsisVertical",
  plus: "Plus",
  close: "X",
  back: "ArrowLeft",
  chevron: "ChevronRight",
  chevronLeft: "ChevronLeft",
  calendar: "CalendarDays",
  down: "ChevronDown",
  up: "ChevronUp",
  check: "Check",
  checks: "CheckCheck",
  mic: "Mic",
  send: "SendHorizontal",
  smile: "Smile",
  sticker: "Sticker",
  gif: "Clapperboard",
  file: "FileText",
  image: "Image",
  camera: "Camera",
  audio: "Headphones",
  poll: "ChartNoAxesColumnIncreasing",
  event: "CalendarDays",
  star: "Star",
  pin: "Pin",
  unpin: "PinOff",
  eye: "Eye",
  eyeOff: "EyeOff",
  archive: "Archive",
  bell: "Bell",
  mute: "BellOff",
  split: "Split",
  undo: "Undo2",
  redo: "Redo2",
  repeat: "Repeat",
  workflow: "Workflow",
  lock: "LockKeyhole",
  shield: "ShieldCheck",
  key: "KeyRound",
  keyboard: "Keyboard",
  help: "CircleHelp",
  logout: "LogOut",
  edit: "Pencil",
  copy: "Copy",
  trash: "Trash2",
  reply: "Reply",
  forward: "Forward",
  info: "Info",
  download: "Download",
  video: "Video",
  play: "Play",
  pause: "Pause",
  wifiOff: "WifiOff",
  stop: "Square",
  link: "Link",
  clock: "Clock3",
  settings: "Settings",
  palette: "Palette",
  sun: "Sun",
  moon: "Moon",
  code: "Code2",
  grid: "LayoutGrid",
  book: "BookOpen",
  lightbulb: "Lightbulb",
  addUser: "UserRoundPlus",
  earth: "Globe",
  heart: "Heart",
  checkCircle: "CircleCheck",
  ban: "Ban",
  idCard: "IdCard",
  tag: "Tag",
  zap: "Zap",
  volume: "Volume2",
  expand: "Maximize2",
  grip: "GripVertical",
  refresh: "RotateCcw",
  list: "ListFilter",
  sparkles: "Sparkles",
  mail: "Mail",
  phoneIncoming: "PhoneIncoming",
  phoneOutgoing: "PhoneOutgoing",
  sendOff: "Send",
  xCircle: "CircleX",
  monitor: "Monitor",
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  crop: "Crop",
  rotate: "RotateCw",
  pen: "PenLine",
  map: "MapPin",
  external: "ExternalLink",
  phone: "Phone",
  options: "List",
  strike: "Strikethrough",
  rocket: "Rocket",
  receipt: "Receipt",
  megaphone: "Megaphone",
  wallet: "Wallet",
  trend: "TrendingUp",
  filter: "Filter",
  upload: "Upload",
};
/**
 * WhatsApp Web's own glyphs, read from web.whatsapp.com (2026-09-25): filled, on a 24 × 24 grid
 * unless a glyph names its own (Material Symbols use 0 -960 960 960), for the places that must
 * look exactly like it (the composer, the tag button).
 */
const WA_GLYPHS: Record<
  string,
  Array<{ d: string; evenodd?: boolean; viewBox?: string }>
> = {
  // wds-ic-switch-account: WhatsApp's "Add to list" button and "Manage lists" (2026-10-09)
  "wa-lists": [
    {
      d: "M645-555q35-35 35-85t-35-85q-35-35-85-35t-85 35q-35 35-35 85t35 85q35 35 85 35t85-35ZM320-330q45-53 108-81.5T560-440q69 0 132 28.5T800-330v-470H320v470Zm0 90q-33 0-56.5-23.5T240-320v-480q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H320ZM160-80q-33 0-56.5-23.5T80-160v-520q0-17 11.5-28.5T120-720q17 0 28.5 11.5T160-680v520h520q17 0 28.5 11.5T720-120q0 17-11.5 28.5T680-80H160Zm371.5-531.5Q520-623 520-640t11.5-28.5Q543-680 560-680t28.5 11.5Q600-657 600-640t-11.5 28.5Q577-600 560-600t-28.5-11.5ZM428-320h264q-29-20-63-30t-69-10q-35 0-69 10t-63 30Zm132-245Z",
      viewBox: "0 -960 960 960",
    },
  ],
  // arrow_drop_down: the filled caret of that button
  "wa-drop-down": [
    { d: "M480-360 280-560h400L480-360Z", viewBox: "0 -960 960 960" },
  ],
  // plus-rounded
  "wa-plus": [
    {
      d: "M11 13H5.5a1 1 0 1 1 0-2H11V5.5a1 1 0 1 1 2 0V11h5.5a1 1 0 1 1 0 2H13v5.5a1 1 0 1 1-2 0V13Z",
    },
  ],
  // wds-ic-sticker-smiley
  "wa-expressions": [
    {
      d: "M8.5 10.25a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm8.5-1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z",
    },
    {
      d: "M16.82 19.98A6.97 6.97 0 0 1 12 22H9.27A7.27 7.27 0 0 1 2 14.73V9.27A7.27 7.27 0 0 1 9.27 2h5.46A7.27 7.27 0 0 1 22 9.27v2.54c0 1.94-.77 3.8-2.15 5.17l-3.03 3ZM14.72 4H9.28A5.27 5.27 0 0 0 4 9.27v5.46A5.27 5.27 0 0 0 9.27 20h2.06a.9.9 0 0 0 .68-.88l-.02-2.26v-.11a5.5 5.5 0 0 1-4.65-2.6.6.6 0 0 1 .03-.6c.12-.2.3-.3.53-.3h5.7a4.8 4.8 0 0 1 3.22-1.23l2.26.01c.5 0 .9-.4.9-.9V9.07H20A5.27 5.27 0 0 0 14.73 4Zm-.71 15.11c0 .15-.01.3-.04.44a4.96 4.96 0 0 0 1.44-.99l3.03-3c.46-.46.83-.99 1.09-1.56-.15.02-.3.03-.46.03h-2.26A2.8 2.8 0 0 0 14 16.84l.02 2.26Z",
      evenodd: true,
    },
  ],
  // ic-mic
  "wa-mic": [
    {
      d: "M12 14a3 3 0 0 1-2.13-.87A3 3 0 0 1 9 11V5q0-1.25.87-2.12A3 3 0 0 1 12 2q1.25 0 2.12.88Q15 3.75 15 5v6q0 1.25-.88 2.13A3 3 0 0 1 12 14m-1 6v-2.07a6.8 6.8 0 0 1-3.94-1.95 7 7 0 0 1-1.99-3.95A.9.9 0 0 1 6 11q.42 0 .71.29.3.29.39.71a4.8 4.8 0 0 0 1.74 2.88A5 5 0 0 0 12 16q1.8 0 3.17-1.14A5 5 0 0 0 16.9 12q.1-.42.39-.71.29-.3.7-.29a.9.9 0 0 1 .93 1.03 7 7 0 0 1-5.92 5.9V20q0 .42-.29.71-.29.3-.71.29a1 1 0 0 1-.71-.29 1 1 0 0 1-.3-.71m1-8q.42 0 .71-.29.3-.29.29-.71V5q0-.42-.29-.71A1 1 0 0 0 12 4q-.42 0-.71.29-.3.29-.3.71v6q0 .42.3.71.29.3.7.29",
    },
  ],
  // ic-notifications-off
  "wa-notifications-off": [
    {
      d: "M16.15 19H5a.97.97 0 0 1-.71-.29A.97.97 0 0 1 4 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h1v-7a6.3 6.3 0 0 1 .85-3.15l1.5 1.5A4.2 4.2 0 0 0 8 10v7h6.2L2.1 4.9a.95.95 0 0 1-.27-.7c0-.28.09-.52.27-.7a.95.95 0 0 1 .7-.27c.28 0 .52.09.7.27l17 17a1 1 0 0 1 0 1.4.95.95 0 0 1-.7.28.95.95 0 0 1-.7-.28L16.15 19ZM13.5 4.2a5.77 5.77 0 0 1 3.25 2.11A5.86 5.86 0 0 1 18 10v2.75c0 .33-.1.58-.31.75a1.05 1.05 0 0 1-1.38-.01.98.98 0 0 1-.31-.77V10c0-1.1-.4-2.04-1.18-2.83a3.85 3.85 0 0 0-3.67-1.07c-.3.07-.57.15-.8.25-.28.12-.56.14-.84.08a1 1 0 0 1-.63-.48.96.96 0 0 1-.14-.69.72.72 0 0 1 .39-.54c.21-.11.44-.21.67-.3l.7-.22v-.7c0-.42.15-.77.44-1.06.29-.3.64-.44 1.06-.44.42 0 .77.15 1.06.44.3.29.44.64.44 1.06v.7ZM12 22c-.5 0-.95-.14-1.34-.41a1.3 1.3 0 0 1-.59-1.11c0-.14.06-.25.17-.34a.5.5 0 0 1 .36-.14h2.8c.13 0 .25.05.36.14.11.09.17.2.17.34 0 .46-.2.83-.6 1.1A2.2 2.2 0 0 1 12 22Z",
    },
  ],
  // wds-ic-broadcast-message
  "wa-broadcast": [
    {
      d: "M17.52 3c1.5 0 2.7 1.2 2.7 2.7v1.36a.95.95 0 0 1-1.9 0V5.7c0-.4-.3-.7-.7-.7H4.29c-.4 0-.7.3-.7.7l.21 10.48c0 .4.3.7.7.7h3.3l-.08 2.13 2.38-2.13h1.85a.95.95 0 0 1-.1 1.9H10.8l-3.5 3a.9.9 0 0 1-.5.2 1 1 0 0 1-1-1v-2.2H4.5c-1.5 0-2.7-1.2-2.7-2.7L1.6 5.71A2.7 2.7 0 0 1 4.3 3h13.22Zm2.16 6.52a1 1 0 0 1 1.41.12l.25.32a7.7 7.7 0 0 1-.28 9.85l-.23.23a1 1 0 0 1-1.39-1.44l.17-.17a5.71 5.71 0 0 0-.05-7.5l-.06-.09a1 1 0 0 1 .18-1.32Z",
    },
    {
      d: "M16.55 11.77a1 1 0 0 1 1.4.13 4.25 4.25 0 0 1-.16 5.76 1 1 0 1 1-1.38-1.44c.67-.64.9-2 .02-3.04a1 1 0 0 1 .12-1.4ZM13.3 13.9a1 1 0 1 1 1.42 1.42 1 1 0 0 1-1.42-1.42Z",
    },
  ],
  // ic-format-bold, ic-format-italic, ic-format-strikethrough, ic-code, ic-format-list-numbered,
  // ic-format-list-bulleted and ic-format-quote: the composer's formatting bar.
  "wa-format-bold": [
    {
      d: "M8.8 19c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V7c0-.55.2-1.02.59-1.41.39-.4.86-.59 1.41-.59h3.52a5 5 0 0 1 3 1c.92.67 1.38 1.6 1.38 2.78 0 .85-.2 1.5-.57 1.96-.39.46-.75.79-1.08.98.42.19.88.53 1.39 1.03.5.5.76 1.25.76 2.25 0 1.48-.54 2.52-1.63 3.11a6.4 6.4 0 0 1-3.04.89H8.8Zm1.02-2.8h2.6c.8 0 1.3-.2 1.47-.61.17-.41.26-.7.26-.89 0-.18-.09-.48-.26-.89-.18-.4-.69-.61-1.54-.61H9.82v3Zm0-5.7h2.33c.55 0 .95-.14 1.2-.43.25-.28.38-.6.38-.95 0-.4-.15-.72-.43-.97a1.6 1.6 0 0 0-1.1-.38H9.82v2.73Z",
    },
  ],
  "wa-format-italic": [
    {
      d: "M6.25 19a1.2 1.2 0 0 1-.89-.36 1.2 1.2 0 0 1-.36-.89c0-.35.12-.65.36-.89s.54-.36.89-.36H9l3-9H9.25a1.2 1.2 0 0 1-.89-.36A1.2 1.2 0 0 1 8 6.25c0-.35.12-.65.36-.89S8.9 5 9.25 5h7.5c.35 0 .65.12.89.36s.36.54.36.89-.12.65-.36.89a1.2 1.2 0 0 1-.89.36H14.5l-3 9h2.25c.35 0 .65.12.89.36s.36.54.36.89-.12.65-.36.89a1.2 1.2 0 0 1-.89.36h-7.5Z",
    },
  ],
  "wa-format-strike": [
    {
      d: "M486-160q-63 0-116.5-33.5T285-283q-9-16-2.5-33.5T306-343q18-10 37.5-3.5T374-322q18 30 48.5 48t65.5 18q44 0 76.5-27t32.5-69q0-20 14-34t34-14q20 0 34.5 14t14.5 34v12q0 79-62.5 129.5T486-160ZM120-480q-17 0-28.5-11.5T80-520q0-17 11.5-28.5T120-560h720q17 0 28.5 11.5T880-520q0 17-11.5 28.5T840-480H120Zm208-165q-17-10-23-29.5t4-36.5q25-47 72-71t101-24q48 0 90.5 20t71.5 58q11 14 7 32t-19 29q-17 12-36.5 9T562-677q-15-17-35-25t-43-8q-27 0-52 11t-38 34q-10 18-29.5 24t-36.5-4Z",
      viewBox: "0 -960 960 960",
    },
  ],
  "wa-format-code": [
    {
      d: "m193-479 155 155q11 11 11 28t-11 28q-11 11-28 11t-28-11L108-452q-6-6-8.5-13T97-480q0-8 2.5-15t8.5-13l184-184q12-12 28.5-12t28.5 12q12 12 12 28.5T349-635L193-479Zm574-2L612-636q-11-11-11-28t11-28q11-11 28-11t28 11l184 184q6 6 8.5 13t2.5 15q0 8-2.5 15t-8.5 13L668-268q-12 12-28 11.5T612-269q-12-12-12-28.5t12-28.5l155-155Z",
      viewBox: "0 -960 960 960",
    },
  ],
  "wa-format-numbered": [
    {
      d: "M150-80q-13 0-21.5-8.5T120-110q0-13 8.5-21.5T150-140h70v-30h-30q-13 0-21.5-8.5T160-200q0-13 8.5-21.5T190-230h30v-30h-70q-13 0-21.5-8.5T120-290q0-13 8.5-21.5T150-320h90q17 0 28.5 11.5T280-280v40q0 17-11.5 28.5T240-200q17 0 28.5 11.5T280-160v40q0 17-11.5 28.5T240-80h-90Zm0-280q-13 0-21.5-8.5T120-390v-80q0-17 11.5-28.5T160-510h60v-30h-70q-13 0-21.5-8.5T120-570q0-13 8.5-21.5T150-600h90q17 0 28.5 11.5T280-560v70q0 17-11.5 28.5T240-450h-60v30h70q13 0 21.5 8.5T280-390q0 13-8.5 21.5T250-360H150Zm38.5-288.5Q180-657 180-670v-150h-30q-13 0-21.5-8.5T120-850q0-13 8.5-21.5T150-880h60q13 0 21.5 8.5T240-850v180q0 13-8.5 21.5T210-640q-13 0-21.5-8.5ZM400-200q-17 0-28.5-11.5T360-240q0-17 11.5-28.5T400-280h400q17 0 28.5 11.5T840-240q0 17-11.5 28.5T800-200H400Zm0-240q-17 0-28.5-11.5T360-480q0-17 11.5-28.5T400-520h400q17 0 28.5 11.5T840-480q0 17-11.5 28.5T800-440H400Zm0-240q-17 0-28.5-11.5T360-720q0-17 11.5-28.5T400-760h400q17 0 28.5 11.5T840-720q0 17-11.5 28.5T800-680H400Z",
      viewBox: "0 -960 960 960",
    },
  ],
  "wa-format-bulleted": [
    {
      d: "M10 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 19H10Zm0-6a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 12c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 13H10Zm0-6a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 6c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 7H10ZM5 20c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41 0-.55.2-1.02.59-1.41.39-.4.86-.59 1.41-.59.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41 0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59Zm0-6c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41 0-.55.2-1.02.59-1.41.39-.4.86-.59 1.41-.59.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41 0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59Zm0-6c-.55 0-1.02-.2-1.41-.59C3.19 7.02 3 6.55 3 6c0-.55.2-1.02.59-1.41C3.98 4.19 4.45 4 5 4c.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41 0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59Z",
    },
  ],
  "wa-format-quote": [
    {
      d: "m262-300 58-100q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 23-5.5 42.5T458-480L331-260q-5 9-14 14.5t-20 5.5q-23 0-34.5-20t-.5-40Zm360 0 58-100q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 23-5.5 42.5T818-480L691-260q-5 9-14 14.5t-20 5.5q-23 0-34.5-20t-.5-40ZM362.5-517.5Q380-535 380-560t-17.5-42.5Q345-620 320-620t-42.5 17.5Q260-585 260-560t17.5 42.5Q295-500 320-500t42.5-17.5Zm360 0Q740-535 740-560t-17.5-42.5Q705-620 680-620t-42.5 17.5Q620-585 620-560t17.5 42.5Q655-500 680-500t42.5-17.5ZM680-560Zm-360 0Z",
      viewBox: "0 -960 960 960",
    },
  ],
  // ic-close
  "wa-close": [
    {
      d: "m12 13.4-4.9 4.9a.95.95 0 0 1-.7.27.95.95 0 0 1-.7-.27.95.95 0 0 1-.28-.7c0-.28.1-.52.28-.7l4.9-4.9-4.9-4.9a.95.95 0 0 1-.28-.7.95.95 0 0 1 .97-.98c.3 0 .53.1.71.28l4.9 4.9 4.9-4.9a.95.95 0 0 1 .7-.27c.28 0 .52.09.7.27.18.18.27.42.27.7 0 .28-.09.52-.27.7L13.4 12l4.9 4.9c.18.18.27.42.27.7 0 .28-.09.52-.27.7a.95.95 0 0 1-.7.27.95.95 0 0 1-.7-.27L12 13.4Z",
    },
  ],
  // wds-ic-send-filled
  "wa-send": [
    {
      d: "M5.4 19.43a.99.99 0 0 1-.95-.1.93.93 0 0 1-.45-.83V14l8-2-8-2V5.5c0-.37.15-.65.45-.84a1 1 0 0 1 .95-.09l15.4 6.5c.42.19.63.5.63.93 0 .43-.21.74-.63.93l-15.4 6.5Z",
    },
  ],
  // The image editor's tools (media composer), read from web.whatsapp.com on 2026-10-29.
  // ic-crop-rotate
  "wa-crop-rotate": [
    {
      d: "M12 24c-2.93 0-5.5-.92-7.71-2.76a11.66 11.66 0 0 1-4.11-6.96c-.05-.29 0-.55.16-.78a.9.9 0 0 1 .66-.4 1 1 0 0 1 .75.21c.22.18.35.4.4.69.43 2.18 1.47 4 3.11 5.48a9.7 9.7 0 0 0 5.84 2.47l-.85-.85a.95.95 0 0 1-.28-.7.95.95 0 0 1 .97-.97c.3 0 .53.09.71.27l3.3 3.3c.12.12.15.25.11.4a.36.36 0 0 1-.29.28A10.5 10.5 0 0 1 12 24Zm-3-7c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V9H6a.97.97 0 0 1-.71-.29A.97.97 0 0 1 5 8c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h1V6c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v9h9c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 18 17h-1v1c0 .28-.1.52-.29.71A.94.94 0 0 1 16 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 15 18v-1H9Zm6-4V9h-4V7h4c.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41v4h-2ZM12 0c2.93 0 5.5.92 7.71 2.75a11.62 11.62 0 0 1 4.11 6.97c.05.29 0 .55-.16.78a.9.9 0 0 1-.66.4.97.97 0 0 1-.75-.21 1.1 1.1 0 0 1-.4-.69 9.68 9.68 0 0 0-3.11-5.47 9.71 9.71 0 0 0-5.84-2.48l.85.85c.18.18.28.42.28.7a.95.95 0 0 1-.97.98.95.95 0 0 1-.71-.28L9.05 1a.39.39 0 0 1-.11-.4.36.36 0 0 1 .29-.27A10.52 10.52 0 0 1 12 0Z",
    },
  ],
  // wds-ic-wand
  "wa-wand": [
    {
      d: "M9.14 6.6a2 2 0 0 0-.65.44L7.07 8.45a2 2 0 0 0 0 2.83l11.1 11.1a2 2 0 0 0 2.82 0l1.42-1.4a2 2 0 0 0 0-2.84l-11.1-11.1a2 2 0 0 0-2.17-.43Zm-.59 3.33.04.05 2.08 2.06 1.42-1.4L10 8.56a.15.15 0 0 0-.22 0l-1.2 1.2a.15.15 0 0 0-.04.17Zm10.92 10.94-7.38-7.4h-.01l1.4-1.43 7.4 7.41a.15.15 0 0 1 0 .21l-1.2 1.21a.17.17 0 0 1-.2 0Z",
      evenodd: true,
    },
    {
      d: "M10.61 3.96a.95.95 0 0 0 1.38 0c.2-.2.3-.43.3-.7v-2a.96.96 0 0 0-.99-.99c-.27 0-.5.1-.7.3-.2.2-.29.42-.29.68v2.02c0 .26.1.49.3.69ZM6.35 6.3a.97.97 0 0 1-1.41 0L2.82 4.19a.97.97 0 0 1-.3-.7c0-.28.1-.52.3-.72a.97.97 0 0 1 1.41 0L6.35 4.9a.97.97 0 0 1 0 1.41ZM4 11.95a.95.95 0 0 0 0-1.38.95.95 0 0 0-.69-.3H1.3a.96.96 0 0 0-.99.99c0 .27.11.5.3.7.2.2.43.29.69.29h2c.26 0 .49-.1.69-.3Zm.23 7.39c.27 0 .5-.1.7-.3l1.42-1.42a.97.97 0 0 0 0-1.41.97.97 0 0 0-.7-.3c-.28 0-.51.1-.71.3l-1.42 1.41a.98.98 0 0 0 0 1.41c.2.2.44.3.7.3ZM17.66 6.3a.97.97 0 0 1-1.41 0 .97.97 0 0 1-.3-.7c0-.28.1-.51.3-.71l1.41-1.42a.98.98 0 0 1 1.41 0 .97.97 0 0 1 0 1.41l-1.4 1.42Z",
    },
  ],
  // ic-edit
  "wa-pencil": [
    {
      d: "M5 19h1.4l9.8-9.78-1.42-1.42L5 17.6V19Zm-1 2a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 20v-2.43a1.98 1.98 0 0 1 .58-1.4L16.2 3.57a2.07 2.07 0 0 1 2.19-.42c.24.1.46.25.66.45L20.43 5c.2.18.34.4.43.64a2.13 2.13 0 0 1 0 1.51c-.09.25-.23.47-.43.67l-12.6 12.6a1.94 1.94 0 0 1-1.4.58H4Z",
    },
  ],
  // ic-match-case
  "wa-text": [
    {
      d: "m6.17 14.9-.8 2.22a.84.84 0 0 1-.32.43.88.88 0 0 1-.5.15.84.84 0 0 1-.74-.39.82.82 0 0 1-.08-.81l3.44-9.2a.88.88 0 0 1 .34-.44c.16-.1.33-.16.52-.16h.7a.95.95 0 0 1 .87.6l3.45 9.22c.12.29.08.55-.1.8a.86.86 0 0 1-.72.38.83.83 0 0 1-.5-.16.9.9 0 0 1-.33-.44l-.78-2.2H6.18Zm.58-1.6h3.28l-1.6-4.55h-.1L6.74 13.3Zm9.88 4.65c-.85 0-1.53-.23-2.03-.69a2.34 2.34 0 0 1-.75-1.81c0-.73.29-1.34.86-1.81a3.36 3.36 0 0 1 2.21-.71 6.27 6.27 0 0 1 2.08.37V13c0-.48-.17-.88-.51-1.18a2 2 0 0 0-1.36-.45 2.46 2.46 0 0 0-1.41.45c-.21.17-.42.23-.6.18a.9.9 0 0 1-.47-.28.7.7 0 0 1-.18-.47c0-.18.1-.34.28-.47.33-.27.7-.47 1.13-.6.41-.12.84-.18 1.27-.18 1.15 0 2 .27 2.58.81.56.54.84 1.36.84 2.44v3.68c0 .21-.07.4-.23.55a.79.79 0 0 1-1.11-.02.8.8 0 0 1-.23-.56v-.13h-.1a2.3 2.3 0 0 1-.95.88c-.4.2-.84.3-1.32.3Zm.3-1.35c.58 0 1.07-.2 1.48-.6.41-.4.61-.87.61-1.4a4.27 4.27 0 0 0-1.8-.42c-.53 0-.95.11-1.25.35-.3.23-.45.54-.45.92 0 .33.14.6.4.82.27.22.6.33 1 .33Z",
    },
  ],
  // ic-crop-square
  "wa-square": [
    {
      d: "M5 21c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V5c0-.55.2-1.02.59-1.41C3.98 3.19 4.45 3 5 3h14c.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41v14c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59H5Zm0-2h14V5H5v14Z",
    },
  ],
  // ic-blur-on
  "wa-blur": [
    {
      d: "M3 14.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15Zm0-4a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15ZM6 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 5 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 6 19Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 5 14c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 6 15Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 5 10c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 6 11Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 5 6c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 6 7Zm4 8.5c-.42 0-.77-.15-1.06-.44A1.4 1.4 0 0 1 8.5 14c0-.42.15-.77.44-1.06.29-.3.64-.44 1.06-.44.42 0 .77.15 1.06.44.3.29.44.64.44 1.06 0 .42-.15.77-.44 1.06-.29.3-.64.44-1.06.44Zm0-4c-.42 0-.77-.15-1.06-.44A1.4 1.4 0 0 1 8.5 10c0-.42.15-.77.44-1.06.29-.3.64-.44 1.06-.44.42 0 .77.15 1.06.44.3.29.44.64.44 1.06 0 .42-.15.77-.44 1.06-.29.3-.64.44-1.06.44Zm0 7.5a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 10 19Zm0-12a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 6c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 10 7Zm0 14.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15Zm0-18a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15Zm4 12c-.42 0-.77-.15-1.06-.44A1.4 1.4 0 0 1 12.5 14c0-.42.15-.77.44-1.06.29-.3.64-.44 1.06-.44.42 0 .77.15 1.06.44.3.29.44.64.44 1.06 0 .42-.15.77-.44 1.06-.29.3-.64.44-1.06.44Zm0-4c-.42 0-.77-.15-1.06-.44A1.4 1.4 0 0 1 12.5 10c0-.42.15-.77.44-1.06.29-.3.64-.44 1.06-.44.42 0 .77.15 1.06.44.3.29.44.64.44 1.06 0 .42-.15.77-.44 1.06-.29.3-.64.44-1.06.44Zm0 7.5a.97.97 0 0 1-.71-.29A.97.97 0 0 1 13 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 14 19Zm0-12a.97.97 0 0 1-.71-.29A.97.97 0 0 1 13 6c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 14 7Zm0 14.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15Zm0-18a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15ZM18 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 17 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 18 19Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 17 14c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 18 15Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 17 10c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 18 11Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 17 6c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 18 7Zm3 7.5a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15Zm0-4a.48.48 0 0 1-.35-.15.48.48 0 0 1 0-.7.48.48 0 0 1 .7 0 .48.48 0 0 1 0 .7c-.1.1-.22.15-.35.15Z",
    },
  ],
  // ic-mood
  "wa-mood": [
    {
      d: "M15.5 11c.42 0 .77-.15 1.06-.44.3-.29.44-.64.44-1.06 0-.42-.15-.77-.44-1.06A1.4 1.4 0 0 0 15.5 8c-.42 0-.77.15-1.06.44-.3.29-.44.64-.44 1.06 0 .42.15.77.44 1.06.29.3.64.44 1.06.44Zm-7 0c.42 0 .77-.15 1.06-.44.3-.29.44-.64.44-1.06 0-.42-.15-.77-.44-1.06A1.4 1.4 0 0 0 8.5 8c-.42 0-.77.15-1.06.44-.3.29-.44.64-.44 1.06 0 .42.15.77.44 1.06.29.3.64.44 1.06.44ZM12 22a10.1 10.1 0 0 1-9.21-6.1A9.74 9.74 0 0 1 2 12a10.1 10.1 0 0 1 6.1-9.21A9.74 9.74 0 0 1 12 2a10.1 10.1 0 0 1 9.21 6.1c.53 1.22.79 2.52.79 3.9s-.26 2.68-.79 3.9a10.1 10.1 0 0 1-5.31 5.31A9.74 9.74 0 0 1 12 22Zm0-2c2.23 0 4.13-.77 5.68-2.32A7.72 7.72 0 0 0 20 12a7.7 7.7 0 0 0-2.32-5.67A7.72 7.72 0 0 0 12 4a7.7 7.7 0 0 0-5.67 2.33A7.72 7.72 0 0 0 4 12c0 2.23.78 4.13 2.33 5.68A7.72 7.72 0 0 0 12 20Zm0-2.5a5.53 5.53 0 0 0 4.65-2.6c.1-.2.1-.4-.02-.6a.57.57 0 0 0-.53-.3H7.9a.6.6 0 0 0-.53.3.6.6 0 0 0-.02.6A5.5 5.5 0 0 0 12 17.5Z",
    },
  ],
  // wds-ic-sticker
  "wa-sticker": [
    {
      d: "M12 22a6.97 6.97 0 0 0 4.82-2.02l3.03-3A7.27 7.27 0 0 0 22 11.8V9.27A7.27 7.27 0 0 0 14.73 2H9.27A7.27 7.27 0 0 0 2 9.27v5.46A7.27 7.27 0 0 0 9.27 22H12ZM9.27 4h5.46A5.27 5.27 0 0 1 20 9.08h-.01v2.04c0 .5-.4.91-.9.91h-2.27A4.81 4.81 0 0 0 12 16.85l.02 2.26a.9.9 0 0 1-.68.88H9.27A5.27 5.27 0 0 1 4 14.73V9.27A5.27 5.27 0 0 1 9.27 4Zm4.7 15.55c.03-.14.04-.29.04-.44l-.02-2.26a2.8 2.8 0 0 1 2.82-2.83l2.26.01c.16 0 .3-.01.46-.03-.26.57-.63 1.1-1.09 1.56l-3.03 3c-.42.42-.9.75-1.44 1Z",
      evenodd: true,
    },
  ],
  // wds-ic-hd-settings
  "wa-hd": [
    {
      d: "M4 20c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V6c0-.55.2-1.02.59-1.41C2.98 4.19 3.45 4 4 4h16c.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41v7h-2V6H4v12h11v2H4Z",
    },
    {
      d: "M7.5 13h2v1.25a.73.73 0 0 0 .75.75.73.73 0 0 0 .75-.75v-4.5a.73.73 0 0 0-.75-.75.73.73 0 0 0-.75.75v1.75h-2V9.75A.73.73 0 0 0 6.75 9a.73.73 0 0 0-.75.75v4.5a.73.73 0 0 0 .75.75.73.73 0 0 0 .75-.75V13Z",
    },
    {
      d: "M13.75 9a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h1.5A2.75 2.75 0 0 0 18 12.25v-.5A2.75 2.75 0 0 0 15.25 9h-1.5Zm.5 4.5v-3h1c.69 0 1.25.56 1.25 1.25v.5c0 .69-.56 1.25-1.25 1.25h-1Zm4.88 7.8a5.7 5.7 0 0 1-.88-.48l-.58.18a.8.8 0 0 1-.9-.34l-.16-.28a.78.78 0 0 1-.1-.52.7.7 0 0 1 .26-.46l.44-.38a3.16 3.16 0 0 1 0-1.04l-.44-.38a.7.7 0 0 1-.26-.45.76.76 0 0 1 .1-.51l.18-.3a.8.8 0 0 1 .38-.32.72.72 0 0 1 .5-.02l.58.18a3.48 3.48 0 0 1 .88-.48l.12-.58a.77.77 0 0 1 .76-.62h.32c.19 0 .35.06.49.18s.23.27.27.46l.12.56a5.7 5.7 0 0 1 .88.48l.58-.18a.8.8 0 0 1 .9.34l.16.28c.1.16.13.33.1.52a.7.7 0 0 1-.26.46l-.44.38a3.16 3.16 0 0 1 0 1.04l.44.38a.7.7 0 0 1 .26.45c.03.18 0 .35-.1.51l-.18.3a.8.8 0 0 1-.38.32.72.72 0 0 1-.5.02l-.58-.18a3.48 3.48 0 0 1-.88.48l-.12.58a.77.77 0 0 1-.76.62H20a.73.73 0 0 1-.5-.18.81.81 0 0 1-.26-.46l-.12-.56Zm1.04-1.2c.44 0 .82-.16 1.13-.47.31-.31.47-.69.47-1.13 0-.44-.16-.82-.47-1.13a1.54 1.54 0 0 0-1.13-.47c-.44 0-.82.16-1.13.47-.31.31-.47.69-.47 1.13 0 .44.16.82.47 1.13.31.31.69.47 1.13.47Z",
      evenodd: true,
    },
  ],
  // ic-content-copy
  "wa-copy": [
    {
      d: "M5 22c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V7c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v13h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 15 22H5Zm4-4c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V4c0-.55.2-1.02.59-1.41C7.98 2.19 8.45 2 9 2h9c.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41v12c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59H9Zm0-2h9V4H9v12Z",
    },
  ],
  // ic-download
  "wa-download": [
    {
      d: "M12 15.57a1.1 1.1 0 0 1-.38-.06.88.88 0 0 1-.32-.21l-3.6-3.6a.92.92 0 0 1-.29-.7 1.03 1.03 0 0 1 1-1.01c.28-.01.52.08.71.29L11 12.14V5c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v7.15l1.88-1.88c.2-.2.43-.3.7-.28a1.02 1.02 0 0 1 1 1.01c.02.27-.08.5-.28.7l-3.6 3.6a.8.8 0 0 1-.32.21 1.1 1.1 0 0 1-.38.06ZM6 20c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41v-2c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v2h12v-2c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v2c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59H6Z",
    },
  ],
  // ic-undo
  "wa-undo": [
    {
      d: "M8 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 7 18c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h6.1c1.05 0 1.96-.33 2.74-1A3.16 3.16 0 0 0 18 13.5c0-1-.39-1.83-1.16-2.5-.78-.67-1.69-1-2.74-1H7.8l1.9 1.9c.18.18.27.42.27.7 0 .28-.09.52-.27.7a.95.95 0 0 1-.7.27.95.95 0 0 1-.7-.27L4.7 9.7a.88.88 0 0 1-.21-.32A1.1 1.1 0 0 1 4.42 9c0-.13.03-.26.07-.37a.88.88 0 0 1 .21-.33l3.6-3.6a.95.95 0 0 1 .7-.27c.28 0 .52.09.7.27.18.18.27.42.27.7 0 .28-.09.52-.27.7L7.8 8h6.3c1.62 0 3 .53 4.16 1.58A5.08 5.08 0 0 1 20 13.5c0 1.57-.58 2.88-1.74 3.93A5.98 5.98 0 0 1 14.1 19H8Z",
    },
  ],
  // ic-redo
  "wa-redo": [
    {
      d: "M16.2 10H9.9c-1.05 0-1.96.33-2.74 1A3.16 3.16 0 0 0 6 13.5c0 1 .39 1.83 1.16 2.5.78.67 1.69 1 2.74 1H16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 16 19H9.9c-1.62 0-3-.52-4.16-1.57A5.08 5.08 0 0 1 4 13.5c0-1.57.58-2.88 1.74-3.92A5.98 5.98 0 0 1 9.9 8h6.3l-1.9-1.9a.95.95 0 0 1-.28-.7.95.95 0 0 1 .97-.98c.3 0 .53.1.71.28l3.6 3.6c.1.1.17.2.21.33a1.2 1.2 0 0 1 0 .75.88.88 0 0 1-.21.32l-3.6 3.6a.95.95 0 0 1-.7.27.95.95 0 0 1-.7-.27.95.95 0 0 1-.28-.7c0-.28.1-.52.28-.7l1.9-1.9Z",
    },
  ],
  // ic-rotate-90-degrees-ccw
  "wa-rotate-left": [
    {
      d: "M13 22a8.74 8.74 0 0 1-3.55-.73.82.82 0 0 1-.5-.53 1.1 1.1 0 0 1 .03-.76c.1-.25.27-.43.52-.54a.9.9 0 0 1 .75.01A6.9 6.9 0 0 0 13 20c1.95 0 3.6-.68 4.96-2.04A6.75 6.75 0 0 0 20 13c0-1.95-.68-3.6-2.05-4.96A6.8 6.8 0 0 0 12.98 6h-.18l.9.9c.18.18.28.42.28.7a.95.95 0 0 1-.97.97.95.95 0 0 1-.71-.27L9.7 5.7a1.03 1.03 0 0 1 0-1.4l2.6-2.6a.95.95 0 0 1 .7-.27c.28 0 .52.09.7.27.18.18.28.42.28.7 0 .28-.1.52-.28.7l-.9.9h.17c2.5 0 4.63.88 6.4 2.63A8.66 8.66 0 0 1 22 13a8.7 8.7 0 0 1-2.63 6.38A8.68 8.68 0 0 1 13 22Zm-6-3.43a1.1 1.1 0 0 1-.38-.06.88.88 0 0 1-.32-.21l-4.6-4.6a.88.88 0 0 1-.21-.32 1.1 1.1 0 0 1 0-.76.88.88 0 0 1 .21-.32l4.6-4.6c.1-.1.2-.17.33-.21a1.1 1.1 0 0 1 .75 0c.11.04.22.11.32.21l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6c-.1.1-.2.17-.33.21a1.1 1.1 0 0 1-.37.06Zm0-2.42L10.15 13 7 9.85 3.85 13 7 16.15Z",
    },
  ],
  // ic-rotate-90-degrees-cw
  "wa-rotate-right": [
    {
      d: "M440-80q-150 0-255-105T80-440q0-150 105.5-255T441-800h7l-36-36q-11-11-11-28t11-28q11-11 28-11t28 11l104 104q5 5 8 12.5t3 15.5q0 8-3 15.5t-8 12.5L468-628q-11 11-28 11t-28-11q-11-11-11-28t11-28l36-36h-7q-117 0-199 81.5T160-440q0 117 81.5 198.5T440-160q29 0 56.5-5.5T550-182q15-7 30-.5t21 21.5q6 15 1 30.5T582-109q-34 14-69.5 21.5T440-80Zm212-148L468-412q-6-6-8.5-13t-2.5-15q0-8 2.5-15t8.5-13l184-184q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l184 184q6 6 8.5 13t2.5 15q0 8-2.5 15t-8.5 13L708-228q-6 6-13 8.5t-15 2.5q-8 0-15-2.5t-13-8.5Zm28-86 126-126-126-126-126 126 126 126Zm0-126Z",
      viewBox: "0 -960 960 960",
    },
  ],
  // ic-chevron-right
  "wa-chevron": [
    {
      d: "M12.6 12 8.7 8.1a.95.95 0 0 1-.27-.7c0-.28.09-.52.27-.7a.95.95 0 0 1 .7-.27c.28 0 .52.09.7.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.95.95 0 0 1-.7.27.95.95 0 0 1-.7-.27.95.95 0 0 1-.27-.7c0-.28.09-.52.27-.7l3.9-3.9Z",
    },
  ],
  // ic-check
  "wa-check": [
    {
      d: "m9.55 15.15 8.47-8.48c.2-.2.44-.3.72-.3.27 0 .51.1.71.3.2.2.3.44.3.72 0 .27-.1.51-.3.71l-9.2 9.2a.96.96 0 0 1-1.4 0L4.55 13a.93.93 0 0 1-.29-.71c.01-.28.12-.51.32-.71a.97.97 0 0 1 1.42 0l3.55 3.57Z",
    },
  ],
  // ic-delete
  "wa-delete": [
    {
      d: "M7 21c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V6a.97.97 0 0 1-.71-.29A.97.97 0 0 1 4 5c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h4c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h4c.28 0 .52.1.71.29.2.19.29.43.29.71h4c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 19 6v13c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59H7ZM17 6H7v13h10V6Zm-7 11c.28 0 .52-.1.71-.29.2-.19.29-.43.29-.71V9c0-.28-.1-.52-.29-.71A.97.97 0 0 0 10 8c-.28 0-.52.1-.71.29A.94.94 0 0 0 9 9v7c0 .28.1.52.29.71.19.2.43.29.71.29Zm4 0c.28 0 .52-.1.71-.29.2-.19.29-.43.29-.71V9c0-.28-.1-.52-.29-.71A.97.97 0 0 0 14 8c-.28 0-.52.1-.71.29A.94.94 0 0 0 13 9v7c0 .28.1.52.29.71.19.2.43.29.71.29Z",
    },
  ],
  // ic-format-align-left
  "wa-align-left": [
    {
      d: "M4 21a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 20c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 21H4Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 16c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 14 17H4Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 12c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 13H4Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 8c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 14 9H4Zm0-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 4c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 5H4Z",
    },
  ],
  // ic-format-align-center
  "wa-align-center": [
    {
      d: "M4 21a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 20c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 21H4Zm4-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 7 16c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h8c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 16 17H8Zm-4-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 12c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 13H4Zm4-4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 7 8c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h8c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 16 9H8ZM4 5a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 4c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 5H4Z",
    },
  ],
  // ic-format-align-right
  "wa-align-right": [
    {
      d: "M4 5a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 4c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 5H4Zm6 4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 8c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 9H10Zm-6 4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 12c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 13H4Zm6 4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 9 16c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h10c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 17H10Zm-6 4a.97.97 0 0 1-.71-.29A.97.97 0 0 1 3 20c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29h16c.28 0 .52.1.71.29.2.19.29.43.29.71 0 .28-.1.52-.29.71A.94.94 0 0 1 20 21H4Z",
    },
  ],
  // ic-circle
  "wa-circle": [
    {
      d: "M12 22a10.1 10.1 0 0 1-9.21-6.1A10 10 0 0 1 2 12q0-2.07.79-3.9A10 10 0 0 1 8.1 2.79 10 10 0 0 1 12 2q2.07 0 3.9.79a10 10 0 0 1 5.31 5.31q.8 1.83.79 3.9 0 2.07-.79 3.9a10 10 0 0 1-5.31 5.31A10 10 0 0 1 12 22m0-2q3.35 0 5.68-2.32A7.7 7.7 0 0 0 20 12q0-3.34-2.32-5.67A7.7 7.7 0 0 0 12 4Q8.66 4 6.33 6.33A7.7 7.7 0 0 0 4 12q0 3.35 2.33 5.68A7.7 7.7 0 0 0 12 20",
    },
  ],
  // ic-pen-size-2
  "wa-line": [
    {
      d: "M5.3 18.7a1 1 0 0 1-.27-.7q0-.43.27-.7l12-12a1 1 0 0 1 .69-.3q.4 0 .71.3.27.27.28.7 0 .42-.28.7l-12 12a1 1 0 0 1-.7.28 1 1 0 0 1-.7-.28",
    },
  ],
  // ic-north-east
  "wa-arrow": [
    {
      d: "M17 8.4 6.1 19.3a1 1 0 0 1-.7.27 1 1 0 0 1-.7-.27 1 1 0 0 1-.28-.7q0-.43.28-.7L15.6 7H10a1 1 0 0 1-.71-.29A1 1 0 0 1 9 6q0-.42.29-.71.29-.3.71-.29h8q.42 0 .71.29.3.29.29.71v8q0 .42-.29.71-.29.3-.71.29a1 1 0 0 1-.71-.29A1 1 0 0 1 17 14z",
    },
  ],
  // ic-block
  "wa-block": [
    {
      d: "M12 22a10.1 10.1 0 0 1-9.21-6.1A9.74 9.74 0 0 1 2 12a10.1 10.1 0 0 1 6.1-9.21A9.74 9.74 0 0 1 12 2a10.1 10.1 0 0 1 9.21 6.1c.53 1.22.79 2.52.79 3.9s-.26 2.68-.79 3.9a10.1 10.1 0 0 1-5.31 5.31A9.74 9.74 0 0 1 12 22Zm0-2a7.81 7.81 0 0 0 4.9-1.7L5.7 7.1A7.95 7.95 0 0 0 4 12c0 2.23.78 4.13 2.33 5.68A7.72 7.72 0 0 0 12 20Zm6.3-3.1A7.95 7.95 0 0 0 20 12a7.7 7.7 0 0 0-2.32-5.67A7.72 7.72 0 0 0 12 4a7.81 7.81 0 0 0-4.9 1.7l11.2 11.2Z",
    },
  ],
  // media-editor-blur-pixelate
  "wa-pixelate": [
    {
      d: "M4.5 4.5h3v3h-3zm0 6h3v3h-3zm0 6h3v3h-3zm3-9h3v3h-3zm0 6h3v3h-3zm6-6h3v3h-3zm0 6h3v3h-3zm-3-9h3v3h-3zm0 6h3v3h-3zm0 6h3v3h-3zm6-12h3v3h-3zm0 6h3v3h-3zm0 6h3v3h-3z",
    },
  ],
};
const glyph = computed(() => WA_GLYPHS[props.name]);
const component = computed(
  () =>
    (icons as Record<string, any>)[aliases[props.name] || props.name] || Circle,
);
</script>
<template>
  <svg
    v-if="glyph"
    :width="size"
    :height="size"
    :viewBox="glyph[0]?.viewBox ?? '0 0 24 24'"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      v-for="(p, i) in glyph"
      :key="i"
      :d="p.d"
      :fill-rule="p.evenodd ? 'evenodd' : undefined"
      :clip-rule="p.evenodd ? 'evenodd' : undefined"
    />
  </svg>
  <component
    v-else
    :is="component"
    :size="size"
    :stroke-width="stroke"
    aria-hidden="true"
  />
</template>
